import { useState } from "react";

const FAQS = [
  {
    q: "Which Florida statutes apply to our association website?",
    a: "For HOAs, the primary statute is Florida Statute 720.303(5), which requires associations with 100 or more parcels to maintain a website with specific official records. Condominiums fall under 718.111(12), cooperatives under 719.104, and 55+ communities must additionally satisfy HOPA (Housing for Older Persons Act) and Fair Housing Act reporting. We map each requirement to your community during the audit.",
  },
  {
    q: "How likely is our website to receive an ADA demand letter?",
    a: "South Florida, particularly Miami-Dade and Broward counties, is the #1 US jurisdiction for website accessibility lawsuits under ADA Title III. Any public-facing association site with inaccessible PDFs, low color contrast, or missing alt text is a realistic target. Bringing your site to WCAG 2.2 AA and documenting the process is the strongest defense.",
  },
  {
    q: "Do we have to make our owner login area accessible too?",
    a: "Yes. Any web content that is available to the public or to residents must meet accessibility standards. The good news: because you know who your users are, we can offer additional accommodations (large-type mode, translated audio summaries) that go well beyond baseline WCAG.",
  },
  {
    q: "How long does a rebuild typically take?",
    a: "A standard HOA or 55+ community rebuild runs 4 to 6 weeks from board approval to launch, including PDF remediation. Larger master-planned communities and condo hi-rises with extensive milestone documentation typically run 6 to 10 weeks. Emergency remediations following a demand letter can be completed in 72 hours.",
  },
  {
    q: "Do you work with our property management company or attorney?",
    a: "Always. Roughly 60% of our engagements come through community association managers (CAMs) and community-association attorneys. We provide direct billing to management companies and produce documentation formatted for board packets and legal files.",
  },
  {
    q: "What happens once our website is fully compliant?",
    a: "Most communities choose to move to a Managed Compliance plan, which includes hosting, statute-change monitoring, and a quarterly compliance certificate for your board minutes. You always keep full ownership of your website and records.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFaqs = FAQS.filter(
    (f) =>
      f.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.a.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <section id="faq" className="py-24 md:py-32 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary-700 font-semibold">
            <i className="ri-question-answer-line" />
            Frequently asked
          </span>
          <h2 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-foreground-950 leading-[1.05]">
            Answers your board actually asks.
          </h2>
          <p className="mt-6 text-foreground-700 text-base leading-relaxed">
            Compiled from twelve years of board workshops, annual meetings, and Zoom Q&As. Don't see
            yours? Call us directly and we'll be happy to help.
          </p>
          <a
            href="#contact"
            className="mt-8 inline-flex items-center gap-2 text-primary-700 hover:text-primary-800 font-semibold cursor-pointer whitespace-nowrap"
          >
            Ask a compliance question
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        <div className="lg:col-span-8">
          <div className="relative mb-6">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search questions (e.g. 720.303, ADA, 55+, timeline, attorney)..."
              className="w-full pl-11 pr-10 py-3.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 p-1 cursor-pointer"
                aria-label="Clear search"
              >
                <i className="ri-close-line text-lg" />
              </button>
            )}
          </div>

          <div className="space-y-3">
            {filteredFaqs.length === 0 ? (
              <div className="rounded-lg border border-background-200 bg-background-100 p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-questionnaire-line text-2xl" />
                </div>
                <h4 className="font-heading font-semibold text-foreground-950 text-lg">No matching questions found</h4>
                <p className="mt-1 text-sm text-foreground-600 max-w-sm mx-auto">
                  Can't find what you're looking for? Call us directly at <a href="tel:+19544444747" className="font-semibold text-primary-700 hover:underline">(954) 444-4747</a> or send us a message.
                </p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="mt-4 text-xs font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
                >
                  Clear search
                </button>
              </div>
            ) : (
              filteredFaqs.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div
                    key={f.q}
                    className={`rounded-lg border transition-colors ${
                      isOpen
                        ? "border-primary-300 bg-primary-50"
                        : "border-background-200 bg-background-100"
                    }`}
                  >
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 cursor-pointer"
                    >
                      <h4 className="font-heading text-lg font-semibold text-foreground-950">
                        <a href={`#faq-${i}`} className="cursor-pointer">
                          {f.q}
                        </a>
                      </h4>
                      <span
                        className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-full transition-transform ${
                          isOpen
                            ? "bg-primary-500 text-background-50 rotate-45"
                            : "bg-background-50 text-foreground-700"
                        }`}
                      >
                        <i className="ri-add-line" />
                      </span>
                    </button>
                    {isOpen && (
                      <div
                        id={`faq-${i}`}
                        className="px-6 pb-6 text-foreground-700 leading-relaxed text-base"
                        dangerouslySetInnerHTML={{ __html: f.a }}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </section>
  );
}